import { createContext } from "react";

import type { AuthUser } from "./id-token";

/**
 * 認証のインターフェース定義。実装（spiff-auth-provider）と分離しておく。
 *
 * IdP を SpiffArena 内蔵 OpenID から Zeus IdP に切り替える際、変わるのは
 * getAuthHeaders / login / logout の中身だけで、この型と利用側は変わらない。
 */

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export type LoginOptions = {
  /** ログイン後に戻る絶対 URL。既定は現在の URL。 */
  readonly returnTo?: string;
  /** 複数 IdP がある場合の選択。既定は VITE_DEFAULT_AUTHENTICATION_IDENTIFIER。 */
  readonly authenticationIdentifier?: string;
};

export type AuthContextValue = {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** IdP へフルページ遷移する（この関数は return しない）。 */
  readonly login: (_options?: LoginOptions) => void;
  readonly logout: () => void;
  /**
   * API が 401 を返したときに呼ばれる。セッションを破棄して再ログインする。
   * API クライアント → 認証実装への唯一の逆方向依存。
   */
  readonly onUnauthorized: () => void;
  /**
   * 各リクエストに付与する認証ヘッダ。実装差分をここに閉じ込める。
   * backend が cookie を更新するため、呼び出しごとに読み直す実装にする。
   */
  readonly getAuthHeaders: () => Record<string, string>;
};

export const AuthContext = createContext<AuthContextValue | null>(null);
